/**
 * @engine/input — Touchscreen Device
 *
 * Multi-touch input device.
 */

import type { TouchProperty, Vec2Like } from '../types';
import { InputDevice } from './InputDevice';
import { ButtonControl } from '../controls/ButtonControl';
import { AxisControl } from '../controls/AxisControl';
import { Vector2Control } from '../controls/Vector2Control';

/**
 * Touch phase (matches W3C TouchEvent)
 */
export type TouchPhase = 'began' | 'moved' | 'stationary' | 'ended' | 'canceled';

/**
 * Individual touch point state
 */
export interface TouchState {
  readonly id: number;
  readonly phase: TouchPhase;
  readonly position: Vec2Like;
  readonly delta: Vec2Like;
  readonly pressure: number;
  readonly radius: Vec2Like;
}

/**
 * Single touch point — not a full device, just a control group for one finger.
 */
export class TouchPoint {
  readonly touchId: number;
  readonly active: ButtonControl;
  readonly position: Vector2Control;
  readonly delta: Vector2Control;
  readonly pressure: AxisControl;
  readonly radius: Vector2Control;

  private _phase: TouchPhase = 'ended';

  constructor(index: number) {
    this.touchId = index;

    this.active = new ButtonControl('active');
    this.position = new Vector2Control('position');
    this.delta = new Vector2Control('delta');
    this.pressure = new AxisControl('pressure', false);
    this.radius = new Vector2Control('radius');
  }

  get phase(): TouchPhase {
    return this._phase;
  }

  get isActive(): boolean {
    return this.active.isPressed;
  }

  updateState(state: TouchState, time: number): void {
    this._phase = state.phase;
    this.active.update(state.phase !== 'ended' && state.phase !== 'canceled', time);
    this.position.update(state.position, time);
    this.delta.update(state.delta, time);
    this.pressure.update(state.pressure, time);
    this.radius.update(state.radius, time);
  }

  reset(): void {
    this._phase = 'ended';
    this.active.reset();
    this.position.reset();
    this.delta.reset();
    this.pressure.reset();
    this.radius.reset();
  }

  /**
   * Get a control by name from this touch point
   */
  getControl(name: string): ButtonControl | AxisControl | Vector2Control | undefined {
    switch (name) {
      case 'active': return this.active;
      case 'position': return this.position;
      case 'delta': return this.delta;
      case 'pressure': return this.pressure;
      case 'radius': return this.radius;
      default: return undefined;
    }
  }
}

/**
 * Touchscreen device — manages multiple touch points.
 */
export class Touchscreen extends InputDevice {
  /** Maximum tracked touches */
  static readonly MAX_TOUCHES = 10;

  /** Active touch controls */
  readonly touches: TouchPoint[] = [];

  /** Number of currently active touches */
  readonly touchCount: AxisControl;

  /** Primary touch (first active) */
  readonly primaryTouch: TouchPoint;

  constructor(index: number = 0) {
    super('touchscreen', index, 'Touchscreen');

    // Create touch controls
    for (let i = 0; i < Touchscreen.MAX_TOUCHES; i++) {
      this.touches.push(new TouchPoint(i));
    }
    this.primaryTouch = this.touches[0];

    // Touch count
    this.touchCount = new AxisControl('touchCount', false);
    this.registerControl(this.touchCount);
  }

  // ============================================================================
  // State Updates
  // ============================================================================

  /**
   * Update from TouchEvent touches
   */
  updateTouches(touches: readonly TouchState[], time: number): void {
    // Reset all touches first
    for (const touch of this.touches) {
      if (touch.phase === 'ended' || touch.phase === 'canceled') {
        touch.active.update(false, time);
      }
    }

    // Update active touches
    for (let i = 0; i < touches.length && i < Touchscreen.MAX_TOUCHES; i++) {
      this.touches[i].updateState(touches[i], time);
    }

    // Update touch count
    let count = 0;
    for (const touch of this.touches) {
      if (touch.active.isPressed) count++;
    }
    this.touchCount.update(count, time);
  }

  /**
   * Get touch by index
   */
  getTouch(index: number): TouchPoint | undefined {
    return this.touches[index];
  }

  /**
   * Get first active touch
   */
  getActiveTouches(): TouchPoint[] {
    return this.touches.filter((t) => t.active.isPressed);
  }

  // ============================================================================
  // Control Resolution
  // ============================================================================

  /**
   * Get control by name.
   * Supports: touchCount, touch0, touch1, ..., touch0/position, etc.
   */
  override getControl(name: string): ButtonControl | AxisControl | Vector2Control | undefined {
    if (name === 'touchCount') return this.touchCount;

    // Check if it's a touch index (touch0)
    const touchMatch = name.match(/^touch(\d+)$/);
    if (touchMatch) {
      // Return the touch's active button as default control
      const idx = parseInt(touchMatch[1], 10);
      const touch = this.touches[idx];
      return touch?.active;
    }

    // Sub-control access (e.g., "touch0/position")
    const parts = name.split('/');
    if (parts.length >= 2) {
      const touchIdxMatch = parts[0].match(/^touch(\d+)$/);
      if (touchIdxMatch) {
        const touch = this.touches[parseInt(touchIdxMatch[1], 10)];
        if (touch) {
          return touch.getControl(parts.slice(1).join('/'));
        }
      }
    }

    return super.getControl(name) as ButtonControl | AxisControl | Vector2Control | undefined;
  }
}
